<script setup lang="ts">
import DropFile from '@legacy/components/common/form/input/DropFile.vue';
import ApiError from '@legacy/js/model/Error';
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import SphCheckbox from '@/components/base/checkbox/';
import { SphFieldset, SphFormField } from '@/components/base/form-field';
import { SphInput } from '@/components/base/input';
import { SphRadioGroup } from '@/components/base/radio-group';
import { SphSelect } from '@/components/base/select';
import { Separator as SphSeparator } from '@/components/base/separator';
import SphSpinner from '@/components/base/spinner/';
import { SphTextarea } from '@/components/base/textarea';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useDialog } from '@/plugins/dialog';

defineOptions({
    compatConfig: { MODE: 3 },
});
const props = defineProps({
    federationId: {
        type: Number,
        required: true,
    },
    allClubs: {
        type: Array<Club>,
        required: true,
    },
    sections: {
        type: Array<Section>,
        required: true,
    },
    selectedSectionIds: {
        type: Array<number>,
        required: true,
    },
});
const emit = defineEmits<{
    cancel: []
    done: []
}>();
const selectedClubIds = defineModel<number[]>('selectedIds', { required: true });
const { i18n } = useI18n();
const { notify } = useNotifications();
const { dialog } = useDialog();

interface Club {
    id: number
    name: string
}
interface Section {
    id: number
    name: string
}
interface EmailOptionsResponse {
    replyTos: string[]
    clubEmployeeRoles: Array<{ id: number, name: string }>
}
interface Filter {
    name: string | number | null
    checked: boolean
    label: string
    children?: Filter[]
}

const loading = ref<boolean>(false);
const sending = ref<boolean>(false);
const apiError = ref<InstanceType<typeof ApiError> | null>(null);
const hasFileError = ref<boolean>(false);

const clubOptions = computed(() =>
    props.allClubs
        .map((c) => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' })),
);

const sectionChildren = [
    ...props.sections.map((section) => ({
        name: `section-${section.id}`,
        checked: props.selectedSectionIds.includes(section.id) || props.selectedSectionIds.length === 0,
        label: section.name,
    })),
    { name: 'section-null', checked: props.selectedSectionIds.length === 0, label: i18n.t('Ohne Bereich') },
];

const federationFilter = ref<Filter>({
    name: 'federation',
    checked: true,
    label: i18n.t('Trägermitarbeiter'),
    children: sectionChildren,
});

const clubEmployeeScope = ref<'none' | 'all' | 'selective'>('none');

const clubEmployeeScopeOptions = [
    { value: 'none', label: i18n.t('Keine Vereinsmitarbeiter') },
    { value: 'all', label: i18n.t('Alle Vereinsmitarbeiter') },
    { value: 'selective', label: i18n.t('Vereinsmitarbeiter selektiv') },
];

const responsibilitiesFilter = ref<Filter>({
    name: 'responsibilities',
    checked: false,
    label: i18n.t('Verantwortlichkeiten'),
    children: [
        { name: 'administrator', label: i18n.t('Administrator'), checked: false },
        { name: 'chiefExecutiveOfficer', label: i18n.t('Vorstandsvorsitzende(r)'), checked: false },
        { name: 'chiefFinancialOfficer', label: i18n.t('Finanzvorstand'), checked: false },
        { name: 'chiefHumanResourcesOfficer', label: i18n.t('Personalvorstand'), checked: false },
        { name: 'dataProtectionOfficer', label: i18n.t('Datenschutzbeauftragte(r)'), checked: false },
    ],
});

const functionsFilter = ref<Filter>({
    name: 'functions',
    checked: false,
    label: i18n.t('Funktionen'),
    children: [],
});

const functionScopes = ref({
    team: false,
    department: false,
    club: false,
});

const ccFilter = ref<Filter>({ name: 'cc', checked: false, label: i18n.t('Kopie an mich') });

const emailOptions = ref<{
    replyTos: Array<{ value: string, label: string }>
}>({
    replyTos: [],
});
const email = ref<{
    replyTo: string | undefined
    subject: string | undefined
    body: string | undefined
    files: Array<{ type: string, name: string, base64: string }>
}>({ replyTo: undefined, subject: undefined, body: undefined, files: [] });

const SUBJECT_MAX_LENGTH = 255;
const BODY_MAX_LENGTH = 10000;
const subjectAtLimit = computed(() => (email.value.subject?.length ?? 0) >= SUBJECT_MAX_LENGTH);
const bodyAtLimit = computed(() => (email.value.body?.length ?? 0) >= BODY_MAX_LENGTH);

const hasAnyRecipientSelected = computed(() => {
    if (ccFilter.value.checked) {
        return true;
    }

    const hasFederationRecipients = federationFilter.value.checked
        && federationFilter.value.children?.some((c) => c.checked);

    if (hasFederationRecipients) {
        return true;
    }

    if (clubEmployeeScope.value === 'all') {
        return true;
    }

    if (clubEmployeeScope.value === 'selective') {
        const hasResponsibilities = responsibilitiesFilter.value.children?.some((c) => c.checked);
        const hasFunctions = functionsFilter.value.children?.some((c) => c.checked);
        return hasResponsibilities || hasFunctions;
    }

    return false;
});

function buildRequestData() {
    return {
        clubIds: selectedClubIds.value,
        sections: federationFilter.value.children?.filter((c) => c.checked).map((c) => {
            const idPart = (c.name! as string).split('-')[1];
            return idPart === 'null' ? null : Number.parseInt(idPart);
        }) || [],
        clubEmployeeScope: clubEmployeeScope.value,
        responsibilities: clubEmployeeScope.value === 'selective'
            ? responsibilitiesFilter.value.children?.filter((c) => c.checked).map((c) => c.name) || []
            : [],
        functions: clubEmployeeScope.value === 'selective'
            ? {
                    roles: functionsFilter.value.children?.filter((c) => c.checked).map((c) => c.name) || [],
                    scopes: {
                        team: functionScopes.value.team,
                        department: functionScopes.value.department,
                        club: functionScopes.value.club,
                    },
                }
            : null,
        cc: ccFilter.value.checked,
        replyTo: email.value.replyTo,
        subject: email.value.subject,
        body: email.value.body,
        files: email.value.files,
    };
}

async function sendEmails() {
    if (sending.value) {
        return;
    }

    apiError.value = null;

    if (functionsFilter.value.checked
        && functionsFilter.value.children?.some((c) => c.checked)
        && !functionScopes.value.team
        && !functionScopes.value.department
        && !functionScopes.value.club) {
        notify.error(i18n.t('Bitte wählen Sie mindestens einen Geltungsbereich für die Funktionen aus.'));
        return;
    }

    const data = buildRequestData();
    const { files: _files, ...countData } = data;

    sending.value = true;
    try {
        const countResponse = await axios.post(`/federations/${props.federationId}/email/club/count`, countData);
        const { recipientsCount, clubCount } = countResponse.data;

        if (recipientsCount === 0) {
            notify.error(i18n.t('Mit der gegebenen Auswahl konnten keine Empfänger ermittelt werden.'));
            return;
        }

        const confirmed = await dialog.confirm({
            title: i18n.t('E-Mail senden'),
            message: i18n.t('Die E-Mail wird an {recipientsCount} Empfänger in {clubCount} Vereinen gesendet. Fortfahren?', {
                recipientsCount,
                clubCount,
            }),
            confirmButtonLabel: i18n.t('Senden'),
        });

        if (!confirmed) {
            return;
        }

        await axios.post(`/federations/${props.federationId}/email/club`, data);
        emit('done');
    } catch (err: any) {
        apiError.value = ApiError.fromResponse(err.response);
        notify.error(err.response?.data?.error?.message ?? i18n.t('Ein Fehler ist aufgetreten.'));
    } finally {
        sending.value = false;
    }
}

function cancel() {
    emit('cancel');
}

function handleFilterToggle(filter: Filter) {
    filter.checked = !filter.checked;
    if (filter.children) {
        filter.children.forEach((child) => {
            child.checked = filter.checked;
        });
    }
}

function handleFunctionsToggle() {
    functionsFilter.value.checked = !functionsFilter.value.checked;
    if (functionsFilter.value.children) {
        functionsFilter.value.children.forEach((child) => {
            child.checked = functionsFilter.value.checked;
        });
    }
    if (functionsFilter.value.checked) {
        functionScopes.value.team = true;
        functionScopes.value.department = true;
        functionScopes.value.club = true;
    }
}

function handleChildToggle(parent: Filter, child: Filter, value: boolean) {
    child.checked = value;
    parent.checked = parent.children!.some((c) => c.checked);
}

function handleFiles(files: Array<File & { base64?: string }>) {
    email.value.files = files.map((file) => ({
        type: file.type,
        name: file.name,
        base64: file.base64 ?? '',
    }));
}

onMounted(() => {
    loading.value = true;
    axios.get<{ data: EmailOptionsResponse }>(`/federations/${props.federationId}/email/club`)
        .then((response) => {
            const data = response.data.data;
            functionsFilter.value.children = data.clubEmployeeRoles.map((d) => ({
                name: d.id,
                checked: false,
                label: d.name,
            }));

            emailOptions.value.replyTos = data.replyTos.map((e) => ({ value: e, label: e }));
            if (emailOptions.value.replyTos.length === 1) {
                email.value.replyTo = emailOptions.value.replyTos[0].value;
            }
        })
        .catch((error) => {
            console.error('Error fetching options:', error);
        })
        .finally(() => {
            loading.value = false;
        })
    ;
});
</script>

<template>
    <SphSpinner
        v-if="loading"
        class="min-h-32"
        variant="centered"
    />
    <div v-else>
        <SphFormField
            :label="i18n.t('Ausgewählte Vereine')"
            class="mb-4"
        >
            <SphSelect
                v-model="selectedClubIds"
                :multiple="{ noTags: true }"
                :options="clubOptions"
                :placeholder="i18n.t('Vereine auswählen')"
                searchable
            />
        </SphFormField>
        <div class="kgr-postfix kgr-postfix--between kgr-postfix--top kgr-row">
            <div class="kgr-postfix__fixed">
                <div class="mb-4">
                    <SphFieldset variant="label">
                        <SphFormField
                            :label="federationFilter.label"
                            layout="choice"
                        >
                            <SphCheckbox
                                :model-value="federationFilter.checked"
                                :name="String(federationFilter.name)"
                                @click="handleFilterToggle(federationFilter)"
                            />
                        </SphFormField>
                        <div
                            v-if="federationFilter.children"
                            class="ml-6"
                        >
                            <SphFieldset variant="label">
                                <SphFormField
                                    v-for="child in federationFilter.children"
                                    :key="String(child.name)"
                                    :label="child.label"
                                    layout="choice"
                                >
                                    <SphCheckbox
                                        :model-value="child.checked"
                                        :name="String(child.name)"
                                        @update:model-value="handleChildToggle(federationFilter, child, $event as boolean)"
                                    />
                                </SphFormField>
                            </SphFieldset>
                        </div>
                    </SphFieldset>
                </div>

                <div class="mb-4">
                    <SphRadioGroup
                        v-model="clubEmployeeScope"
                        :options="clubEmployeeScopeOptions"
                    />
                </div>

                <div
                    v-if="clubEmployeeScope === 'selective'"
                    class="ml-6"
                >
                    <div class="mb-4">
                        <SphFieldset variant="label">
                            <SphFormField
                                :label="responsibilitiesFilter.label"
                                layout="choice"
                            >
                                <SphCheckbox
                                    :model-value="responsibilitiesFilter.checked"
                                    :name="String(responsibilitiesFilter.name)"
                                    @click="handleFilterToggle(responsibilitiesFilter)"
                                />
                            </SphFormField>
                            <div
                                v-if="responsibilitiesFilter.children"
                                class="ml-6"
                            >
                                <SphFieldset variant="label">
                                    <SphFormField
                                        v-for="child in responsibilitiesFilter.children"
                                        :key="String(child.name)"
                                        :label="child.label"
                                        layout="choice"
                                    >
                                        <SphCheckbox
                                            :model-value="child.checked"
                                            :name="String(child.name)"
                                            @update:model-value="handleChildToggle(responsibilitiesFilter, child, $event as boolean)"
                                        />
                                    </SphFormField>
                                </SphFieldset>
                            </div>
                        </SphFieldset>
                    </div>

                    <div class="mb-4">
                        <SphFieldset variant="label">
                            <SphFormField
                                :disabled="functionsFilter.children!.length === 0"
                                :label="functionsFilter.label"
                                layout="choice"
                                title="Diese Werte können in den Formularvorgaben für Mitarbeiter bearbeitet werden"
                            >
                                <SphCheckbox
                                    :model-value="functionsFilter.checked"
                                    :name="String(functionsFilter.name)"
                                    @click="handleFunctionsToggle()"
                                />
                            </SphFormField>
                            <div
                                v-if="functionsFilter.children"
                                class="ml-6"
                            >
                                <SphFieldset variant="label">
                                    <SphFormField
                                        v-for="child in functionsFilter.children"
                                        :key="String(child.name)"
                                        :label="child.label"
                                        layout="choice"
                                    >
                                        <SphCheckbox
                                            :model-value="child.checked"
                                            :name="String(child.name)"
                                            @update:model-value="handleChildToggle(functionsFilter, child, $event as boolean)"
                                        />
                                    </SphFormField>
                                </SphFieldset>

                                <div
                                    v-if="functionsFilter.checked"
                                    class="mt-4"
                                >
                                    <div class="mb-2 whitespace-nowrap">
                                        {{ $t('Geltungsbereich der Funktionen') }}
                                    </div>
                                    <SphFormField
                                        :label="$t('Teamweit')"
                                        layout="choice"
                                    >
                                        <SphCheckbox
                                            v-model="functionScopes.team"
                                            name="scope-team"
                                        />
                                    </SphFormField>
                                    <SphFormField
                                        :label="$t('Abteilungsweit')"
                                        layout="choice"
                                    >
                                        <SphCheckbox
                                            v-model="functionScopes.department"
                                            name="scope-department"
                                        />
                                    </SphFormField>
                                    <SphFormField
                                        :label="$t('Vereinsweit')"
                                        layout="choice"
                                    >
                                        <SphCheckbox
                                            v-model="functionScopes.club"
                                            name="scope-club"
                                        />
                                    </SphFormField>
                                </div>
                            </div>
                        </SphFieldset>
                    </div>
                </div>

                <SphSeparator class="mb-4" orientation="horizontal" />

                <div class="mb-4">
                    <SphFormField
                        :label="ccFilter.label"
                        layout="choice"
                    >
                        <SphCheckbox
                            :name="String(ccFilter.name)"
                            :model-value="ccFilter.checked"
                            @click="ccFilter.checked = !ccFilter.checked"
                        />
                    </SphFormField>
                </div>
            </div>
            <div class="kgr-postfix__fixed space-y-4 w-full ml-12">
                <SphFormField
                    :label="$t('Antwortadresse*')"
                    :errors="apiError?.messagesForField('replyTo', true)"
                >
                    <SphSelect
                        v-model="email.replyTo"
                        :options="emailOptions.replyTos"
                    />
                </SphFormField>
                <div>
                    <SphFormField
                        :errors="apiError?.messagesForField('subject', true)"
                        :label="$t('Betreff*')"
                    >
                        <SphInput
                            v-model="email.subject"
                            :maxlength="SUBJECT_MAX_LENGTH"
                        />
                    </SphFormField>
                    <p
                        v-if="subjectAtLimit"
                        class="text-sm text-neutral-600 mt-1"
                    >
                        {{ $t('Maximal {count} Zeichen', { count: SUBJECT_MAX_LENGTH }) }}
                    </p>
                </div>
                <div>
                    <SphFormField
                        :errors="apiError?.messagesForField('body', true)"
                        :label="$t('Nachricht*')"
                    >
                        <SphTextarea
                            v-model="email.body"
                            :maxlength="BODY_MAX_LENGTH"
                            class="h-92"
                            resizable
                        />
                    </SphFormField>
                    <p
                        v-if="bodyAtLimit"
                        class="text-sm text-neutral-600 mt-1"
                    >
                        {{ $t('Maximal {count} Zeichen', { count: BODY_MAX_LENGTH.toLocaleString('de-DE') }) }}
                    </p>
                </div>
                <DropFile
                    multiple
                    emit-base64-files
                    show-size
                    :max-total-size="20"
                    @files="handleFiles"
                    @error="hasFileError = true"
                    @error-clear="hasFileError = false"
                />
                <p class="text-sm text-neutral-600 mt-1">
                    {{ i18n.t('Alle Anhänge zusammen dürfen höchstens 20 MB groß sein.') }}
                </p>
            </div>
        </div>
        <br />
        <div class="kgr-postfix kgr-postfix--between">
            <div class="kgr-postfix__fixed">
                <SphButton @click="cancel">
                    {{ i18n.t('Abbrechen') }}
                </SphButton>
            </div>
            <div class="kgr-postfix__fixed">
                <SphButton
                    :disabled="sending || hasFileError || !email.replyTo || !email.subject || !email.body || selectedClubIds.length === 0 || !hasAnyRecipientSelected"
                    :loading="sending"
                    variant="primary"
                    @click="sendEmails"
                >
                    {{ i18n.t('E-Mail senden') }}
                </SphButton>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
